import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi, clearSession, getAdmin } from "./adminApi";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { LogOut, ExternalLink, RotateCcw, Save, Eye } from "lucide-react";
import LeadsManager from "./LeadsManager";
import {
  SettingsEditor, HeroEditor, AboutEditor, CoursesEditor,
  FacultyEditor, AchievementsEditor, TestimonialsEditor, ContactEditor, TickerEditor, OffersEditor, FooterEditor,
} from "./SectionEditors";

const TABS = [
  { id: "leads", label: "Leads" },
  { id: "settings", label: "Settings" },
  { id: "hero", label: "Hero" },
  { id: "ticker", label: "Ticker" },
  { id: "about", label: "About" },
  { id: "courses", label: "Courses" },
  { id: "offers", label: "Offers" },
  { id: "faculty", label: "Faculty" },
  { id: "achievements", label: "Achievements" },
  { id: "testimonials", label: "Testimonials" },
  { id: "contact", label: "Contact" },
  { id: "footer", label: "Footer" },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const admin = getAdmin();
  const [content, setContent] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("leads");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi.get("/site/content");
        setContent(data);
        setOriginal(JSON.stringify(data));
      } catch (e) {
        toast.error("Failed to load content");
      }
    })();
  }, []);

  const dirty = content && JSON.stringify(content) !== original;

  const save = async () => {
    if (!content) return;
    setSaving(true);
    try {
      await adminApi.put("/admin/content", { data: content });
      setOriginal(JSON.stringify(content));
      toast.success("Content saved · live in seconds");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!confirm("Reset all content to defaults? Your current changes will be lost.")) return;
    try {
      const { data } = await adminApi.post("/admin/content/reset");
      setContent(data.data);
      setOriginal(JSON.stringify(data.data));
      toast.success("Reset to defaults");
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  const logout = () => {
    clearSession();
    nav("/admin/login", { replace: true });
  };

  const setSection = (key) => (val) => setContent((c) => ({ ...c, [key]: val }));

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="bg-white border-b border-border sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="font-display text-lg font-semibold tracking-tight">Sunbright <span className="text-muted-foreground font-normal">CMS</span></div>
            {admin && <span className="hidden sm:inline text-xs text-muted-foreground">· {admin.email}</span>}
          </div>
          <div className="flex items-center gap-2">
            <a href="/" target="_blank" rel="noopener noreferrer" data-testid="admin-view-site" className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border text-sm hover:border-primary/40 hover:text-primary transition-colors">
              <Eye className="h-4 w-4" /> View site
              <ExternalLink className="h-3 w-3" />
            </a>
            <Button variant="outline" onClick={logout} data-testid="admin-logout-btn" size="sm">
              <LogOut className="h-4 w-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        {!content ? (
          <p className="text-sm text-muted-foreground">Loading dashboard…</p>
        ) : (
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-6">
              <TabsList className="flex flex-wrap h-auto bg-white border border-border p-1">
                {TABS.map((t) => (
                  <TabsTrigger key={t.id} value={t.id} data-testid={`tab-${t.id}`} className="data-[state=active]:bg-primary data-[state=active]:text-white">
                    {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              {tab !== "leads" && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={reset} data-testid="admin-reset-btn">
                    <RotateCcw className="h-4 w-4 mr-1.5" /> Reset
                  </Button>
                  <Button onClick={save} disabled={!dirty || saving} data-testid="admin-save-btn" className="bg-primary text-white hover:bg-[#059669]">
                    <Save className="h-4 w-4 mr-1.5" />
                    {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
                  </Button>
                </div>
              )}
            </div>

            <TabsContent value="leads"><LeadsManager /></TabsContent>
            <TabsContent value="settings"><SettingsEditor data={content.settings || {}} onChange={setSection("settings")} /></TabsContent>
            <TabsContent value="hero"><HeroEditor data={content.hero || {}} onChange={setSection("hero")} /></TabsContent>
            <TabsContent value="ticker"><TickerEditor data={content.ticker || {}} onChange={setSection("ticker")} /></TabsContent>
            <TabsContent value="about"><AboutEditor data={content.about || {}} onChange={setSection("about")} /></TabsContent>
            <TabsContent value="courses"><CoursesEditor data={content.courses || {}} onChange={setSection("courses")} /></TabsContent>
            <TabsContent value="offers"><OffersEditor data={content.offers || {}} onChange={setSection("offers")} /></TabsContent>
            <TabsContent value="faculty"><FacultyEditor data={content.faculty || {}} onChange={setSection("faculty")} /></TabsContent>
            <TabsContent value="achievements"><AchievementsEditor data={content.achievements || {}} onChange={setSection("achievements")} /></TabsContent>
            <TabsContent value="testimonials"><TestimonialsEditor data={content.testimonials || {}} onChange={setSection("testimonials")} /></TabsContent>
            <TabsContent value="contact"><ContactEditor data={content.contact || {}} onChange={setSection("contact")} /></TabsContent>
            <TabsContent value="footer"><FooterEditor data={content.footer || {}} onChange={setSection("footer")} /></TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}
